import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { PartnerIndirectCost, PartnerIndirectCostViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { PartnerIndirectCostPageBody } from "./PartnerIndirectCostPageBody";
import { PartnerIndirectCostPageHeader } from "./PartnerIndirectCostPageHeader";

const routeName = "partnerIndirectCost";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  partnerId?: number = 0;
}

export const partnerIndirectCostPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Indirect Cost",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const PartnerIndirectCostPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [indirectCost, setIndirectCost] = useState<PartnerIndirectCost>({} as PartnerIndirectCost);
  const [viewResource, setViewResource] = useState<PartnerIndirectCostViewResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [indirectCost] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.partnerIndirectCosts.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${partnerIndirectCostPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`
    );

  const fetch = (id: number) => {
    ApiService.partnerIndirectCosts.get(id).then((response) => {
      if (!id && optionalParams.partnerId) response.partnerId = optionalParams.partnerId;

      setIndirectCost(response);
      resetUnsavedChanges([response]);
    });
  };

  const pageHeader = (
    <PartnerIndirectCostPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={partnerIndirectCostPageRoute.handle}
      indirectCost={indirectCost}
      fetch={fetch}
      reroute={reroute}
      crudMode={params.crudMode}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <PartnerIndirectCostPageBody
      crudMode={params.crudMode}
      indirectCost={indirectCost}
      setIndirectCost={setIndirectCost}
      viewResource={viewResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="lg"
      header={pageHeader}
      content={pageBody}
    />
  );
};
